import {
  INTRANET_CONTENT_KIND_LABELS,
  PermissionCode,
  intranetKnowledgeCategoryPath,
  type IntranetFile,
} from '@gb-ticket/shared-types';
import { ArrowLeftIcon, Loader2Icon, PencilIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { AckBanner } from '@/products/intranet/components/ack-banner';
import { IntranetColumns } from '@/products/intranet/components/intranet-columns';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import { EditorLibraryPanel } from '@/products/intranet/components/editor-library-panel';
import { FeedbackWidget } from '@/products/intranet/components/feedback-widget';
import { NoticeForm } from '@/products/intranet/components/notice-form';
import { CurrentFileDownload, FileVersions } from '@/products/intranet/components/file-versions';
import { IntranetBody } from '@/products/intranet/components/intranet-body';
import {
  useArchiveIntranetContent,
  useIntranetContent,
  useRestoreIntranetContent,
} from '@/products/intranet/features/intranet/use-intranet';
import {
  useDeleteIntranetFile,
  useIntranetFiles,
} from '@/products/intranet/features/intranet/use-intranet-library';
import { INTRANET_LIBRARY_PATH, intranetEditPath } from '@/products/intranet/intranet-paths';

export function LibraryDetailPage() {
  const copy = useVoiceCopy();
  const { slug } = useParams();
  const me = useMe();
  const content = useIntranetContent(slug);
  const files = useIntranetFiles(slug);
  const removeFile = useDeleteIntranetFile(slug ?? '');
  const archive = useArchiveIntranetContent();
  const restore = useRestoreIntranetContent();
  const [pendingDelete, setPendingDelete] = useState<IntranetFile | null>(null);
  usePageTitle(content.data?.title ?? 'Wissensdatenbank');
  const canEdit = hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE);

  const back = (
    <Button asChild variant="ghost" size="sm" className="-ml-2">
      <Link to={INTRANET_LIBRARY_PATH}>
        <ArrowLeftIcon aria-hidden />
        Zur Wissensdatenbank
      </Link>
    </Button>
  );

  async function archivePage() {
    if (!slug) {
      return;
    }
    try {
      await archive.mutateAsync(slug);
      toast.success('Seite archiviert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Archivieren fehlgeschlagen.');
    }
  }

  async function restorePage() {
    if (!slug) {
      return;
    }
    try {
      await restore.mutateAsync(slug);
      toast.success('Seite ist wieder ein Entwurf.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Zurückholen fehlgeschlagen.');
    }
  }

  async function confirmDelete() {
    if (!pendingDelete || !slug) {
      return;
    }
    try {
      await removeFile.mutateAsync(pendingDelete.id);
      toast.success(
        pendingDelete.isCurrent && versions.length > 1
          ? 'Datei gelöscht. Die nächstältere Version ist jetzt aktuell.'
          : 'Datei gelöscht.',
      );
      setPendingDelete(null);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  if (content.isPending) {
    return (
      <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Eintrag wird geladen …
      </p>
    );
  }

  if (content.isError) {
    const missing =
      content.error instanceof ApiRequestError && content.error.error.statusCode === 404;
    return (
      <section className="space-y-4">
        {back}
        <p role="alert" className="text-sm text-destructive">
          {missing
            ? copy('intranet.library.not-released')
            : 'Der Eintrag konnte nicht geladen werden.'}
        </p>
      </section>
    );
  }

  const item = content.data;
  const archived = item.state === 'archived';
  const versions = files.data?.items ?? [];
  const currents = versions.filter((file) => file.isCurrent);
  const onDelete = canEdit ? (file: IntranetFile) => setPendingDelete(file) : undefined;

  return (
    <IntranetColumns>
      <article className="min-w-0 space-y-4">
        {back}
        <header className="space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">{INTRANET_CONTENT_KIND_LABELS[item.kind]}</Badge>
            {archived ? (
              <Badge variant="outline">Archiv</Badge>
            ) : item.isDraft ? (
              <Badge variant="outline">Entwurf</Badge>
            ) : null}
            {item.category ? <Badge variant="secondary">{item.category.name}</Badge> : null}
            {item.leadingCategory ? (
              <Link to={intranetKnowledgeCategoryPath(item.leadingCategory.slug)}>
                {item.leadingCategory.name}
              </Link>
            ) : null}
            {(item.furtherCategories ?? []).length > 0 ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span>Weitere Kategorien</span>
                {(item.furtherCategories ?? []).map((category) => (
                  <Link key={category.id} to={intranetKnowledgeCategoryPath(category.slug)}>
                    {category.name}
                  </Link>
                ))}
              </span>
            ) : null}
            {item.publishedAt ? <span>{formatDate(item.publishedAt)}</span> : null}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h1 className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
              {item.title}
            </h1>
            {canEdit ? (
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={intranetEditPath(item.slug)}>
                    <PencilIcon aria-hidden />
                    Bearbeiten
                  </Link>
                </Button>
                {archived ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={restore.isPending}
                    onClick={() => void restorePage()}
                  >
                    Zurückholen
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={archive.isPending}
                    onClick={() => void archivePage()}
                  >
                    Archivieren
                  </Button>
                )}
              </div>
            ) : null}
          </div>
          {item.teaser ? <p className="text-base text-muted-foreground">{item.teaser}</p> : null}
          <AckBanner content={item} />
        </header>

        {files.isPending ? (
          <p className="text-sm text-muted-foreground">Datei wird geladen …</p>
        ) : currents.length === 0 ? (
          <CurrentFileDownload file={undefined} />
        ) : (
          <div className="flex flex-col gap-3">
            {currents.map((file) => (
              <CurrentFileDownload
                key={file.id}
                file={file}
                onDelete={onDelete}
                deletePending={removeFile.isPending}
              />
            ))}
          </div>
        )}

        {item.body ? (
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
            <IntranetBody body={item.body} expandImages />
          </div>
        ) : null}

        <FileVersions
          files={versions.filter((file) => !file.isCurrent)}
          onDelete={onDelete}
          deletePending={removeFile.isPending}
        />

        {item.isDraft || archived ? null : <FeedbackWidget key={item.id} content={item} />}
        {item.isDraft || archived ? null : <NoticeForm key={`${item.id}-meldung`} content={item} />}

        {canEdit ? <EditorLibraryPanel slug={item.slug} files={versions} /> : null}

        <ConfirmDeleteDialog
          open={pendingDelete !== null}
          title="Datei löschen?"
          description={
            pendingDelete
              ? pendingDelete.isCurrent && versions.length > 1
                ? `„${pendingDelete.filename}“ (Version ${pendingDelete.version}) wird entfernt. Die nächstältere Version wird dann aktuell.`
                : `„${pendingDelete.filename}“ (Version ${pendingDelete.version}) wird endgültig entfernt.`
              : ''
          }
          pending={removeFile.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void confirmDelete()}
        />
      </article>
    </IntranetColumns>
  );
}
